import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Flex, Grid } from '$components/layouts';
import { ISearchContentResult, ISearchPersonResult, ISearchSerieResult } from '$models';
import { mq } from '$lib/helpers';
import { key } from '~/utils/key';
import { useIntersectionObserver, useTranslation } from '$hooks';
import { formatString } from '$lib/helpers';
import { Loading } from '$components/elements/loading/loading';
import { Text } from '$components/elements/text';
import { Button } from '$components/elements/button';
import { Link } from '$components/elements/link';
import { Image } from '$components/elements/image';

type Props = {
    authors?: ISearchPersonResult;
    articles?: ISearchContentResult;
    isLoadingMore?: boolean;
    onLoadMore?: () => void;
    series?: ISearchSerieResult;
    title: string;
};

export const SearchResultList: FC<Props> = ({ series, authors, articles, title, isLoadingMore, onLoadMore }) => {
    const { translate } = useTranslation();
    const infScrollIntersection = useRef<HTMLDivElement>(null);
    const [allowInfinity, setAllowInfinity] = useState<boolean>(false);
    const intersection = useIntersectionObserver(infScrollIntersection);

    useEffect(() => {
        if (intersection?.isIntersecting && allowInfinity) {
            loadMore();
        }
    }, [intersection?.isIntersecting]);

    const loadMore = () => {
        if (!allowInfinity) {
            setAllowInfinity(true);
        }
        onLoadMore?.();
    };

    const listedResults = useMemo(() => {
        if (series) {
            return series.series?.map((s) => (
                <ResultCard
                    url={s.serieLink}
                    key={key(s)}
                    imageSrc={s.serieImage || series.defaultIconUrl}
                    title={s.serieTitle}
                />
            ));
        } else if (authors) {
            return authors.authors?.map((author) => (
                <ResultCard
                    url={author.url}
                    key={`${author.id}`}
                    imageSrc={author.image?.[0] || authors.defaultIconUrl}
                    title={author.fullName}
                />
            ));
        } else {
            return articles?.content?.map((article) => (
                <ResultCard
                    url={article.url}
                    key={key(article)}
                    teaser={article.teaser}
                    imageSrc={article.image || articles.defaultIconUrl}
                    title={article.heading}
                />
            ));
        }
    }, [series, authors, articles]);

    if (!listedResults?.length) {
        return <></>;
    }

    return (
        <div>
            {!!title && <h5>{title}</h5>}
            <Grid
                flow="row"
                columns={`repeat(${articles ? 1 : 2}, minmax(0, 1fr))`}
                columnGap="small"
                rowGap="small"
                breakpoints={{
                    [mq('md')]: {
                        columns: `repeat(${articles ? 2 : 4}, minmax(0, 1fr))`,
                    },
                    [mq('lg')]: {
                        columns: `repeat(${articles ? 3 : 6}, minmax(0, 1fr))`,
                    },
                }}
            >
                {listedResults}
            </Grid>
            <div ref={infScrollIntersection} />
            {(authors || series || articles)?.hasNextPage && !allowInfinity && (
                <LoadMore justifyContent="center">
                    <Button loading={isLoadingMore} onClick={loadMore}>
                        {formatString(
                            translate('search.searchResultPage.showMore'),
                            (authors || series || articles)?.totalDocumentsFound || 0
                        )}
                    </Button>
                </LoadMore>
            )}
            {isLoadingMore && <Loading />}
        </div>
    );
};

const LoadMore = styled(Flex)(({ theme }) => ({
    margin: `${theme.space[8]} 0`,
}));

type ResultCardProps = {
    imageSrc?: string;
    title?: string;
    teaser?: string;
    url?: string;
};

const ResultCard: FC<ResultCardProps> = ({ imageSrc, title, teaser, url }) => (
    <Link href={url}>
        <Flex column gap="extrasmall">
            <ResultImageContainer>
                {imageSrc && <Image height={220} src={imageSrc || ''} objectFit="cover" />}
            </ResultImageContainer>
            <Text font="regular" fontWeight="Bold">
                {title}
            </Text>
            <Text>{teaser}</Text>
        </Flex>
    </Link>
);

const ResultImageContainer = styled.div(({ theme }) => ({
    background: theme.colors.grey5,
    height: 220,
    width: '100%',
    borderRadius: theme.general.borderRadius,
    overflow: 'hidden',
}));
